/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {IxModule, ModalService, ToastService} from '@siemens/ix-angular';
import {DppService} from '../../../../services/dpp.service';
import {
  getSubmodelIcon,
  getSubmodelMetadata,
  SUBMODEL_CREATE_ROUTE_SEGMENT,
  SubmodelType,
} from '../../../../models/dpp/submodel-types';
import {SubmodelReference} from '../../../../models/dpp/SubmodelReference';
import {DppSubmodelItem} from '../../../../models/dpp/submodel-item';
import {ModalCreateSubmodelComponent} from '../modal-create-submodel/modal-create-submodel.component';

@Component({
  selector: 'app-dpp-detail-view',
  templateUrl: './dpp-detail-view.component.html',
  styleUrl: './dpp-detail-view.component.scss',
  standalone: true,
  imports: [TranslateModule, IxModule],
})
export class DppDetailViewComponent implements OnInit, OnDestroy {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly dppService: DppService = inject(DppService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService =
    inject(TranslateService);
  private readonly modalService: ModalService = inject(ModalService);

  dppId = '';
  headerTitle = '';
  globalAssetId = '';
  aasId = '';
  thumbnailUrl: string | null = null;
  submodels: DppSubmodelItem[] = [];
  isLoading = true;
  hasError = false;
  defaultSubmodelIcon = 'document';

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.dppId = params['id'];
      this.loadDppData(this.dppId);
    });
  }

  ngOnDestroy(): void {
    this.revokeThumbnailUrl();
  }

  private async loadDppData(dppId: string): Promise<void> {
    try {
      const shellData = await this.dppService.getDppShell(dppId);

      this.aasId = shellData.id || dppId;
      this.globalAssetId = shellData.assetInformation?.globalAssetId || '—';
      this.headerTitle = shellData.displayName?.[0]?.text || `${dppId}`;

      try {
        const file = await this.dppService.getThumbnail(dppId);
        this.revokeThumbnailUrl();
        this.thumbnailUrl = URL.createObjectURL(file.blob);
      } catch {
        this.revokeThumbnailUrl();
        this.thumbnailUrl = null;
      }
      this.submodels = shellData.submodels.map(
        (submodel: SubmodelReference) => {
          const metadata = getSubmodelMetadata(submodel.semanticId);
          return {
            id: submodel.id,
            name: metadata?.type,
            version: metadata?.version,
            icon: getSubmodelIcon(metadata?.type, this.defaultSubmodelIcon),
          };
        },
      );
    } catch {
      this.hasError = true;
      await this.toastService.show({
        message: this.translateService.instant(
          'dpp.passport_detail_view.load_error_message',
        ),
        type: 'error',
      });
      this.navigateToPassportList();
    } finally {
      this.isLoading = false;
    }
  }

  onSubmodelSelected(submodel?: DppSubmodelItem): void {
    if (
      !submodel?.name ||
      !SUBMODEL_CREATE_ROUTE_SEGMENT[submodel.name as SubmodelType]
    ) {
      return;
    }

    this.router.navigate(
      ['/app/dpp/view-passport', this.dppId, 'submodel', submodel.id],
      {queryParams: {passportName: this.headerTitle}},
    );
  }

  async openCreateSubmodelModal(): Promise<void> {
    const existingTypes = this.submodels
      .map((s) => s.name)
      .filter(Boolean) as SubmodelType[];

    const instance = await this.modalService.open({
      content: ModalCreateSubmodelComponent,
      size: '480',
      centered: true,
      data: {existingTypes},
    });
    instance.onClose.on((selectedType: SubmodelType) => {
      const segment = SUBMODEL_CREATE_ROUTE_SEGMENT[selectedType];
      if (!segment) {
        return;
      }
      this.router.navigate([
        '/app/dpp/view-passport',
        this.dppId,
        'submodel',
        segment,
      ]);
    });
  }

  onSubmodelDelete(): void {
    // TODO: Implement delete confirmation and deletion logic
  }

  trackBySubmodel(index: number, item: DppSubmodelItem): string {
    return item.id || item.name || `submodel-${index}`;
  }

  navigateToPassportList(): void {
    this.router.navigate(['/app/dpp/list-passports']);
  }

  private revokeThumbnailUrl(): void {
    if (this.thumbnailUrl) {
      URL.revokeObjectURL(this.thumbnailUrl);
    }
  }
}
